/**
 * Enhanced AI Analysis Utilities
 * Provides comprehensive portfolio analysis including risk metrics, scenario projections, and AI commentary
 */

/**
 * Calculate portfolio volatility (annualized standard deviation)
 * @param {Array} holdings - Array of holdings with amounts and expected returns
 * @returns {number} Annualized volatility percentage
 */
export const calculateVolatility = (holdings) => {
  if (!holdings || holdings.length === 0) return 0;

  // Asset class volatility assumptions (annualized %)
  const volatilityMap = {
    'Stocks': 18,
    'Bonds': 5,
    'Real Estate': 12,
    'Commodities': 20,
    'Cash': 1,
    'Crypto': 60,  // Cryptocurrencies are highly volatile
    'Cryptocurrency': 60  // ⭐ NEW: Support Cryptocurrency as investment category
  };

  const totalValue = holdings.reduce((sum, h) => sum + h.amount, 0);
  if (totalValue === 0) return 0;

  // Calculate weighted average volatility
  let weightedVolatility = 0;
  holdings.forEach(holding => {
    const weight = holding.amount / totalValue;
    const vol = volatilityMap[holding.category] || 15; // Default to 15% if unknown
    weightedVolatility += weight * vol;
  });

  return parseFloat(weightedVolatility.toFixed(2));
};

/**
 * Calculate Sharpe Ratio
 * @param {number} expectedReturn - Portfolio expected return (%)
 * @param {number} riskFreeRate - Risk-free rate (default 3%)
 * @param {number} volatility - Portfolio volatility (%)
 * @returns {number} Sharpe ratio
 */
export const calculateSharpeRatio = (expectedReturn, riskFreeRate = 3, volatility) => {
  if (volatility === 0) return 0;
  const sharpe = (expectedReturn - riskFreeRate) / volatility;
  return parseFloat(sharpe.toFixed(2));
};

/**
 * Calculate portfolio expected return
 * @param {Array} holdings - Array of holdings with amounts and expected returns
 * @returns {number} Weighted average expected return (%)
 */
export const calculateExpectedReturn = (holdings) => {
  if (!holdings || holdings.length === 0) return 0;

  const totalValue = holdings.reduce((sum, h) => sum + h.amount, 0);
  if (totalValue === 0) return 0;

  let weightedReturn = 0;
  holdings.forEach(holding => {
    const weight = holding.amount / totalValue;
    const returnRate = holding.return || holding.expectedReturn || 5; // Default 5%
    weightedReturn += weight * returnRate;
  });

  return parseFloat(weightedReturn.toFixed(2));
};

/**
 * Generate scenario projections (best, median, worst case)
 * @param {number} currentValue - Current portfolio value
 * @param {number} monthlyContribution - Monthly contribution amount
 * @param {number} years - Number of years to project
 * @param {number} expectedReturn - Expected annual return (%)
 * @param {number} volatility - Portfolio volatility (%)
 * @returns {Object} Scenario projections with best, median, worst cases
 */
export const generateScenarioProjections = (
  currentValue,
  monthlyContribution,
  years,
  expectedReturn,
  volatility
) => {
  // Validate and sanitize inputs
  currentValue = Number(currentValue) || 0;
  monthlyContribution = Number(monthlyContribution) || 0;
  years = Math.max(1, Math.min(Number(years) || 10, 50)); // Limit 1-50 years
  expectedReturn = Math.max(-20, Math.min(Number(expectedReturn) || 5, 30)); // Limit -20% to 30%
  volatility = Math.max(0, Math.min(Number(volatility) || 10, 50)); // Limit 0% to 50%

  // Convert to decimals
  const annualReturn = expectedReturn / 100;
  const annualVolatility = volatility / 100;

  // Calculate scenario rates with reasonable bounds
  const bestCaseRate = Math.max(0, Math.min(annualReturn + (annualVolatility * 0.5), 0.15)); // Max 15%
  const medianRate = annualReturn;
  const worstCaseRate = Math.max(-0.05, annualReturn - (annualVolatility * 0.5)); // Min -5%

  // Calculate using simplified compound interest formula
  // FV = PV(1+r)^n + PMT * [((1+r)^n - 1) / r]

  const calculateFutureValue = (rate) => {
    // Handle edge case where rate is 0 or very close to 0
    if (Math.abs(rate) < 0.0001) {
      return currentValue + (monthlyContribution * 12 * years);
    }

    // Calculate present value growth
    const pvGrowth = currentValue * Math.pow(1 + rate, years);

    // Calculate annuity (contributions) growth
    const annualContribution = monthlyContribution * 12;
    const annuityGrowth = annualContribution * ((Math.pow(1 + rate, years) - 1) / rate);

    const total = pvGrowth + annuityGrowth;

    // Sanity check: if result is unrealistic, use simpler calculation
    if (!isFinite(total) || total > 1e12) {
      // Fallback: use simple calculation without compounding on contributions
      return currentValue * Math.pow(1 + rate, years) + (annualContribution * years);
    }

    return total;
  };

  const bestValue = calculateFutureValue(bestCaseRate);
  const medianValue = calculateFutureValue(medianRate);
  const worstValue = calculateFutureValue(worstCaseRate);

  // Final validation: ensure all values are reasonable
  const maxReasonableValue = currentValue * 100 + (monthlyContribution * 12 * years * 50); // Max reasonable growth

  return {
    best: Math.max(0, Math.min(Math.round(bestValue), maxReasonableValue)),
    median: Math.max(0, Math.min(Math.round(medianValue), maxReasonableValue)),
    worst: Math.max(0, Math.round(worstValue)),
    years
  };
};

/**
 * Calculate diversification score based on allocation spread
 * @param {Array} portfolioData - Portfolio allocation data
 * @returns {Object} Score and rating
 */
export const calculateDiversificationScore = (portfolioData) => {
  if (!portfolioData || portfolioData.length === 0) {
    return { score: 0, rating: 'Poor' };
  }

  // Count asset classes with meaningful allocation (>5%)
  const activeClasses = portfolioData.filter(p => p.allocation > 5).length;
  const totalClasses = portfolioData.length;

  // Calculate Herfindahl-Hirschman Index (HHI) for concentration
  let hhi = 0;
  portfolioData.forEach(p => {
    const share = p.allocation / 100;
    hhi += share * share;
  });

  // Convert HHI to diversification score (0-100)
  // HHI ranges from 0 (perfect diversification) to 1 (complete concentration)
  const concentrationPenalty = hhi * 100;
  const diversificationBonus = (activeClasses / totalClasses) * 100;

  const score = Math.min(100, Math.round((diversificationBonus + (100 - concentrationPenalty)) / 2));

  let rating = 'Poor';
  if (score >= 80) rating = 'Excellent';
  else if (score >= 60) rating = 'Good';
  else if (score >= 40) rating = 'Fair';

  return { score, rating };
};

/**
 * Generate AI commentary based on portfolio analysis
 * @param {Object} analysisData - Complete portfolio analysis data
 * @returns {Array} Array of commentary insights
 */
export const generateAICommentary = (analysisData) => {
  const commentary = [];
  const {
    portfolioData,
    totalValue,
    cryptoValue,
    accountsValue,
    expectedReturn,
    volatility,
    sharpeRatio,
    diversificationScore
  } = analysisData;

  // Asset allocation commentary
  const stockAllocation = portfolioData.find(p => p.name === 'Stocks')?.allocation || 0;
  const bondAllocation = portfolioData.find(p => p.name === 'Bonds')?.allocation || 0;
  const cryptoAllocation = totalValue > 0 ? (cryptoValue / totalValue) * 100 : 0;
  const commodityAllocation = portfolioData.find(p => p.name === 'Commodities')?.allocation || 0;

  // Stock concentration analysis
  if (stockAllocation > 80) {
    commentary.push({
      type: 'warning',
      title: 'High Equity Concentration',
      message: `Your portfolio is ${stockAllocation}% stocks, which exposes you to significant market risk. Consider adding low-correlation assets like bonds (target: 20-30%) or commodities (5-10%) to reduce volatility.`
    });
  } else if (stockAllocation > 60 && stockAllocation <= 80) {
    commentary.push({
      type: 'info',
      title: 'Equity-Heavy Portfolio',
      message: `With ${stockAllocation}% in stocks, you have an aggressive growth strategy. This is appropriate for younger investors with long time horizons, but consider gradually increasing bond allocation as you approach retirement.`
    });
  }

  // Bond allocation analysis
  if (bondAllocation < 10 && stockAllocation > 50) {
    commentary.push({
      type: 'suggestion',
      title: 'Low Fixed Income Allocation',
      message: `You have only ${bondAllocation}% in bonds. Adding 15-25% in bonds could provide stability during market downturns and reduce overall portfolio volatility by 20-30%.`
    });
  }

  // Crypto allocation analysis
  if (cryptoAllocation > 15) {
    commentary.push({
      type: 'warning',
      title: 'High Cryptocurrency Exposure',
      message: `Cryptocurrency represents ${cryptoAllocation.toFixed(1)}% of your total portfolio. Due to extreme volatility (60%+ annually), experts recommend limiting crypto to 5-10% of total holdings to manage risk.`
    });
  } else if (cryptoAllocation > 0 && cryptoAllocation <= 10) {
    commentary.push({
      type: 'positive',
      title: 'Balanced Crypto Allocation',
      message: `Your ${cryptoAllocation.toFixed(1)}% crypto allocation is within recommended limits. This provides growth potential while limiting exposure to high volatility.`
    });
  }

  // Commodities allocation
  if (commodityAllocation === 0 && totalValue > 50000) {
    commentary.push({
      type: 'suggestion',
      title: 'Consider Adding Commodities',
      message: 'Commodities (gold, silver, oil) can provide inflation protection and have low correlation with stocks. Consider allocating 5-10% to enhance diversification.'
    });
  }

  // Diversification commentary
  if (diversificationScore < 40) {
    commentary.push({
      type: 'warning',
      title: 'Poor Diversification',
      message: 'Your portfolio is highly concentrated. Spread investments across at least 3-4 different asset classes with meaningful allocations (>10% each) to reduce risk.'
    });
  } else if (diversificationScore >= 80) {
    commentary.push({
      type: 'positive',
      title: 'Excellent Diversification',
      message: `Your diversification score of ${diversificationScore} indicates well-balanced portfolio construction. This helps protect against sector-specific downturns.`
    });
  }

  // Sharpe Ratio commentary
  if (sharpeRatio > 1.0) {
    commentary.push({
      type: 'positive',
      title: 'Strong Risk-Adjusted Returns',
      message: `Your Sharpe ratio of ${sharpeRatio} indicates excellent risk-adjusted returns. You're being well-compensated for the level of risk taken.`
    });
  } else if (sharpeRatio < 0.5) {
    commentary.push({
      type: 'warning',
      title: 'Low Risk-Adjusted Returns',
      message: `Your Sharpe ratio of ${sharpeRatio} suggests suboptimal risk-adjusted returns. Consider rebalancing toward higher-return assets or reducing volatility through better diversification.`
    });
  }

  // Volatility commentary
  if (volatility > 18) {
    commentary.push({
      type: 'warning',
      title: 'High Portfolio Volatility',
      message: `Your portfolio volatility of ${volatility}% is above average. This means larger swings in value. Consider increasing bond allocation to reduce volatility if this makes you uncomfortable.`
    });
  } else if (volatility < 8) {
    commentary.push({
      type: 'info',
      title: 'Conservative Portfolio',
      message: `Your portfolio volatility of ${volatility}% indicates a conservative approach. While stable, consider whether this aligns with your growth goals and time horizon.`
    });
  }

  return commentary;
};

/**
 * Generate country-specific recommendations
 * @param {string} country - Country code (US or UK)
 * @param {Array} accounts - User's accounts
 * @returns {Array} Country-specific recommendations
 */
export const generateCountrySpecificRecommendations = (country, accounts = []) => {
  const recommendations = [];

  if (country === 'US') {
    // Check for 401(k)
    const has401k = accounts.some(a => a.type === '401(k)');
    if (!has401k) {
      recommendations.push({
        title: 'Maximize 401(k) Contributions',
        description: 'Contribute at least enough to get employer matching (typically 3-6% of salary). This is free money that can add thousands to your retirement savings.',
        priority: 'high',
        impact: 'Up to 50-100% instant return on contributions',
        countrySpecific: true
      });
    }

    // Check for IRA
    const hasIRA = accounts.some(a => a.type === 'IRA' || a.type === 'Roth IRA');
    if (!hasIRA) {
      recommendations.push({
        title: 'Open an IRA or Roth IRA',
        description: 'IRAs offer tax advantages and can save thousands in taxes. Roth IRA contributions grow tax-free. 2024 limit: $7,000 ($8,000 if 50+).',
        priority: 'high',
        impact: 'Tax savings of $1,400-$2,600 annually',
        countrySpecific: true
      });
    }

    // HSA recommendation
    const hasHSA = accounts.some(a => a.type === 'HSA');
    if (!hasHSA) {
      recommendations.push({
        title: 'Consider a Health Savings Account (HSA)',
        description: 'Triple tax advantage: deductible contributions, tax-free growth, tax-free withdrawals for medical expenses. Can be used as retirement savings.',
        priority: 'medium',
        impact: 'Additional $4,150+ tax savings annually',
        countrySpecific: true
      });
    }
  } else if (country === 'UK') {
    // Check for ISA
    const hasISA = accounts.some(a => a.type === 'ISA');
    if (!hasISA) {
      recommendations.push({
        title: 'Open a Stocks & Shares ISA',
        description: 'ISAs allow tax-free investment growth. Annual allowance: £20,000. Any gains and income are completely tax-free.',
        priority: 'high',
        impact: '0% tax on investment gains',
        countrySpecific: true
      });
    }

    // Check for Personal Pension
    const hasPension = accounts.some(a => a.type === 'Personal Pension');
    if (!hasPension) {
      recommendations.push({
        title: 'Contribute to Personal Pension',
        description: 'Get 25% tax relief on contributions (higher earners get 40-45%). Employer contributions may also apply.',
        priority: 'high',
        impact: '25-45% instant boost from tax relief',
        countrySpecific: true
      });
    }

    // Lifetime ISA for retirement
    const hasLISA = accounts.some(a => a.type === 'Lifetime ISA');
    if (!hasLISA) {
      recommendations.push({
        title: 'Consider a Lifetime ISA',
        description: 'For under-40s: 25% government bonus on contributions up to £4,000/year. Can be used for first home or retirement.',
        priority: 'medium',
        impact: '£1,000 free annually from government',
        countrySpecific: true
      });
    }
  }

  return recommendations;
};

/**
 * Aggregate all portfolio holdings for comprehensive analysis
 * ⭐ UPDATED: Accounts are now containers, not holdings. We only analyze actual investments.
 * @param {Array} investments - Traditional investments (includes crypto as category now)
 * @param {Array} cryptocurrencies - Legacy crypto holdings (for backward compatibility)
 * @param {Array} accounts - Account containers (NOT included in portfolio calculation)
 * @returns {Object} Aggregated portfolio data
 */
export const aggregatePortfolioHoldings = (investments = [], cryptocurrencies = [], accounts = []) => {
  const holdings = [];

  // Add traditional investments (now includes Cryptocurrency category)
  investments.forEach(inv => {
    holdings.push({
      name: inv.name,
      amount: inv.amount,
      category: inv.category,
      return: inv.return || 5,
      type: 'investment',
      accountId: inv.accountId || null,
      accountName: inv.accountName || 'Unassigned'
    });
  });

  // Add cryptocurrency holdings (legacy support - will be migrated to investments)
  cryptocurrencies.forEach(crypto => {
    holdings.push({
      name: crypto.name || crypto.symbol,
      amount: crypto.value || crypto.balance || 0,
      category: 'Cryptocurrency',
      return: crypto.expectedReturn || 10, // Crypto typically higher expected return
      type: 'crypto'
    });
  });

  // ⭐ REMOVED: No longer treat account balances as cash holdings
  // Accounts are containers - their balance is calculated from investments held in them
  // This fixes the bug where accounts were incorrectly included in portfolio analysis

  const totalValue = holdings.reduce((sum, h) => sum + h.amount, 0);
  const cryptoValue = holdings.filter(h => h.category === 'Cryptocurrency').reduce((sum, h) => sum + h.amount, 0);
  const investmentsValue = holdings.filter(h => h.type === 'investment').reduce((sum, h) => sum + h.amount, 0);

  return {
    holdings,
    totalValue,
    cryptoValue,
    accountsValue: 0, // ⭐ Always 0 - accounts are not part of portfolio
    investmentsValue
  };
};
